import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import Toast from 'react-native-root-toast';
import moment from 'moment';

import {
	PRODUCT_EXPIRY_DATE_POLICY,
	STOCK_DELIVERY_ENDS,
} from '../../../common/constants';
import {
	MODIFY_STOCK_DELIVERY_ITEM,
	RECEIVE_STOCK_DELIVERY_ITEMS_WITH_EXPIRY,
} from '../../../common/graphql/stockDeliveryItem';
import { serializeDateKeyForTimezone } from '../../../common/utils/warehousing/formatExpiryBatchSummary';
import { isProductExpiryDateRequired } from '../../../common/utils/warehousing/productExpiryPolicy';
import { getLocalTime } from '../../../common/utils/time/time';

import { normalizeBatchRows } from './StockDeliveryDetailHelpers';

// Batch capture state and actions for the "Stock item with batches" dialog.
const useStockDeliveryReceive = ({
	stockDelivery,
	deliveryItems,
	timezone,
	tenantSettings,
	refetch,
}) => {
	const [batchCaptureItemId, setBatchCaptureItemId] = useState(null);
	const [batchCaptureRows, setBatchCaptureRows] = useState([]);
	const [batchCaptureError, setBatchCaptureError] = useState('');
	const [modifyStockDeliveryItemMutation, { loading: isUpdatingQuantity }] = useMutation(MODIFY_STOCK_DELIVERY_ITEM);
	const [receiveStockDeliveryItemsWithExpiryMutation, { loading: isCapturingExpiry }] = useMutation(RECEIVE_STOCK_DELIVERY_ITEMS_WITH_EXPIRY);

	const activeBatchCaptureItem = useMemo(() => deliveryItems.find(item => item.id === batchCaptureItemId), [batchCaptureItemId, deliveryItems]);

	const openReceiveDialog = useCallback((item) => {
		if (!item) {
			return;
		}

		const expiryTimezone = item.expiryPolicyContext?.timezone || timezone;
		const existingBatches = normalizeBatchRows(item.expiryBatches || [], expiryTimezone).filter(batch => Number(batch.quantity) > 0);
		const sourceIsWarehouse = stockDelivery?.source === STOCK_DELIVERY_ENDS.WAREHOUSE;
		if (!sourceIsWarehouse && existingBatches.length) {
			setBatchCaptureRows(existingBatches);
		}
		else {
			setBatchCaptureRows([{
				quantity: Math.max(0, Math.round(Number(item.quantity) || 0)),
				expiryDate: '',
				supplierBatchCode: '',
			}]);
		}
		setBatchCaptureError('');
		setBatchCaptureItemId(item.id);
	}, [stockDelivery?.source, timezone]);

	const closeReceiveDialog = useCallback(() => {
		setBatchCaptureItemId(null);
		setBatchCaptureRows([]);
		setBatchCaptureError('');
	}, []);

	const submitReceiveWithExpiry = useCallback(async () => {
		if (!activeBatchCaptureItem?.id) {
			return;
		}

		const expiryTimezone = activeBatchCaptureItem.expiryPolicyContext?.timezone || timezone;
		const sourceIsWarehouse = stockDelivery?.source === STOCK_DELIVERY_ENDS.WAREHOUSE;
		const cleanedRows = normalizeBatchRows(batchCaptureRows || [], expiryTimezone).filter(row => row.quantity > 0 || row.expiryDate || row.supplierBatchCode);
		const requiredQuantity = Math.max(0, Math.round(Number(activeBatchCaptureItem.quantity) || 0));

		if (!sourceIsWarehouse && !cleanedRows.length && requiredQuantity > 0) {
			setBatchCaptureError('Add at least one batch quantity.');
			return;
		}

		if (!sourceIsWarehouse) {
			const hasDateWithoutQuantity = cleanedRows.some(row => (row.expiryDate || row.supplierBatchCode) && !Number(row.quantity));
			if (hasDateWithoutQuantity) {
				setBatchCaptureError('Each batch with date/code must include quantity.');
				return;
			}
		}

		const rowsWithQuantity = cleanedRows.filter(row => row.quantity > 0);
		if (!sourceIsWarehouse && !rowsWithQuantity.length && requiredQuantity > 0) {
			setBatchCaptureError('Add at least one batch quantity.');
			return;
		}

		const totalQuantity = sourceIsWarehouse
			? (batchCaptureRows || []).reduce((acc, row) => acc + Math.max(0, Math.round(Number(row.quantity) || 0)), 0)
			: rowsWithQuantity.reduce((acc, row) => acc + row.quantity, 0);

		if (totalQuantity !== requiredQuantity) {
			setBatchCaptureError(`Batch quantities must equal ${requiredQuantity} units.`);
			return;
		}

		if (!sourceIsWarehouse) {
			const hasInvalidDate = rowsWithQuantity.some(row => row.expiryDate && !moment(row.expiryDate, 'YYYY-MM-DD', true).isValid());
			if (hasInvalidDate) {
				setBatchCaptureError('Use YYYY-MM-DD format for expiry dates.');
				return;
			}

			const currentDateKey = getLocalTime(expiryTimezone).format('YYYY-MM-DD');
			const hasExpiryDateTodayOrEarlier = rowsWithQuantity.some(row => (
				row.expiryDate &&
				!moment(row.expiryDate, 'YYYY-MM-DD', true)
					.isAfter(moment(currentDateKey, 'YYYY-MM-DD', true), 'day')
			));
			if (hasExpiryDateTodayOrEarlier) {
				setBatchCaptureError('Expiry dates must be after today.');
				return;
			}

			const expiryDateRequired = activeBatchCaptureItem.expiryPolicyContext?.policy
				? activeBatchCaptureItem.expiryPolicyContext.policy === PRODUCT_EXPIRY_DATE_POLICY.REQUIRED
				: isProductExpiryDateRequired({
					product: activeBatchCaptureItem.product,
					tenantSettings,
				});
			if (expiryDateRequired && rowsWithQuantity.some(row => !row.expiryDate)) {
				setBatchCaptureError('Expiry date is required for this product.');
				return;
			}
		}

		try {
			await receiveStockDeliveryItemsWithExpiryMutation({
				variables: {
					input: [{
						stockDeliveryItemId: activeBatchCaptureItem.id,
						batches: sourceIsWarehouse
							? []
							: rowsWithQuantity.map(row => ({
								quantity: row.quantity,
								expiryDate: serializeDateKeyForTimezone(row.expiryDate, expiryTimezone),
								supplierBatchCode: row.supplierBatchCode || null,
							})),
					}],
				},
			});
			closeReceiveDialog();
			Toast.show('Item stocked with expiry capture');
			try {
				await refetch();
			}
			catch (refetchError) {
				Toast.show('Item was stocked, but the page could not be refreshed');
			}
		}
		catch (error) {
			setBatchCaptureError(error?.message || 'Unable to stock item with expiry details.');
		}
	}, [
		activeBatchCaptureItem,
		batchCaptureRows,
		closeReceiveDialog,
		receiveStockDeliveryItemsWithExpiryMutation,
		refetch,
		stockDelivery?.source,
		tenantSettings,
		timezone,
	]);

	const confirmDeliveryQuantityChange = useCallback(async (nextQuantity) => {
		if (!activeBatchCaptureItem?.id) {
			return;
		}

		try {
			await modifyStockDeliveryItemMutation({
				variables: {
					input: {
						id: activeBatchCaptureItem.id,
						quantity: Math.max(0, Math.round(Number(nextQuantity) || 0)),
					},
				},
			});
			await refetch();
			setBatchCaptureError('');
			Toast.show('Delivery quantity updated');
		}
		catch (error) {
			setBatchCaptureError(error?.message || 'Unable to update delivery quantity.');
		}
	}, [activeBatchCaptureItem?.id, modifyStockDeliveryItemMutation, refetch]);

	return {
		activeBatchCaptureItem,
		batchCaptureRows,
		setBatchCaptureRows,
		batchCaptureError,
		openReceiveDialog,
		closeReceiveDialog,
		submitReceiveWithExpiry,
		confirmDeliveryQuantityChange,
		isCapturingExpiry,
		isUpdatingQuantity,
	};
};

export default useStockDeliveryReceive;
